/**
 * TEMPORARY HACK. Delete with this folder.
 *
 * UCI Lux administrators need to move a user onto another Raptor config.
 * The catalog endpoint (`/v1/enums/raptor_legacy_configs`) is internal-only,
 * so the options below are hardcoded. The update itself is the existing
 * client-admin route:
 *   PUT /v1/raptor_legacy_configs/{client_id}/{user_id}/{config_name}
 *   POST that same path when the user has no config yet.
 *
 * Do not render this for any other client. A permanent solution assigns
 * pages through resources, not a config name.
 */
import React, { FC, useState } from 'react';
import { Listbox } from '@headlessui/react';
import { FiChevronDown } from 'react-icons/fi';
import axios from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import GenericModal from '../../components/ui/GenericModal';
import FastAPIInstance from '../../services/risksystem3-public-api';
import {
  UCILUX_AUDITOR_READ_ONLY_CONFIG,
  UCILUX_LEGACY_CONFIGS_QUERY_KEY,
} from './uciluxAuditorReadOnly.hack';

const UCILUX_CONFIG_OPTIONS: { configName: string; label: string }[] = [
  { configName: 'ucilux_funds', label: 'Standard' },
  { configName: 'ucilux_read_only_funds', label: 'Read Only' },
  {
    configName: UCILUX_AUDITOR_READ_ONLY_CONFIG,
    label: 'Auditor Read Only',
  },
];

const labelForConfig = (configName: string | undefined): string => {
  if (!configName) return 'No config';
  return (
    UCILUX_CONFIG_OPTIONS.find((option) => option.configName === configName)
      ?.label ?? configName
  );
};

const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error) && error.response?.data) {
    if (typeof error.response.data.detail === 'string') {
      return error.response.data.detail;
    }

    if (typeof error.response.data === 'string') {
      return error.response.data;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An error occurred. Please try again.';
};

interface Props {
  clientId: string;
  userId: string;
  userName: string;
  currentConfigName?: string;
  configsReady: boolean;
  configsFailed: boolean;
  isCurrentUser: boolean;
}

const UciluxUserConfigSelector: FC<Props> = ({
  clientId,
  userId,
  userName,
  currentConfigName,
  configsReady,
  configsFailed,
  isCurrentUser,
}) => {
  const queryClient = useQueryClient();
  const [pendingConfigName, setPendingConfigName] = useState<string | null>(
    null,
  );
  const [saveError, setSaveError] = useState<string | null>(null);

  const knownConfig = UCILUX_CONFIG_OPTIONS.some(
    (option) => option.configName === currentConfigName,
  );
  const options =
    currentConfigName && !knownConfig
      ? [
          ...UCILUX_CONFIG_OPTIONS,
          { configName: currentConfigName, label: currentConfigName },
        ]
      : UCILUX_CONFIG_OPTIONS;

  const saveConfig = useMutation({
    mutationFn: async (configName: string) => {
      const url = `/v1/raptor_legacy_configs/${clientId}/${userId}/${encodeURIComponent(configName)}`;
      if (currentConfigName) {
        await FastAPIInstance.put(url);
      } else {
        await FastAPIInstance.post(url);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [UCILUX_LEGACY_CONFIGS_QUERY_KEY, clientId],
      });
      setPendingConfigName(null);
      setSaveError(null);
    },
    onError: (error) => {
      setSaveError(getErrorMessage(error));
    },
  });

  const closeConfirmation = () => {
    if (saveConfig.isPending) return;
    setPendingConfigName(null);
    setSaveError(null);
  };

  const selectorDisabled = !configsReady || saveConfig.isPending;
  const currentConfigLabel = labelForConfig(currentConfigName);
  const pendingConfigLabel = labelForConfig(pendingConfigName ?? undefined);

  if (configsFailed) {
    return <span className="text-sm text-red-600">Unavailable</span>;
  }

  return (
    <>
      <div className="w-56">
        <Listbox
          value={currentConfigName ?? ''}
          onChange={(nextConfigName: string) => {
            if (!nextConfigName || nextConfigName === currentConfigName) return;
            setSaveError(null);
            setPendingConfigName(nextConfigName);
          }}
          disabled={selectorDisabled}
        >
          <div className="relative">
            <Listbox.Button
              aria-label={`Raptor config for ${userName}`}
              className={`h-7.5 relative w-full cursor-pointer rounded border border-mainblue bg-white px-2.5 py-1 text-left text-sm font-normal focus:outline-none focus:ring-2 focus:ring-mainblue ${selectorDisabled ? 'cursor-not-allowed opacity-70' : ''}`}
            >
              <span className="block truncate">
                {configsReady
                  ? labelForConfig(currentConfigName)
                  : 'Loading...'}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <FiChevronDown
                  className="h-4 w-4 text-mainblue"
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>
            <Listbox.Options
              anchor="bottom start"
              transition
              className="z-10 w-[var(--button-width)] origin-top overflow-auto rounded-md border border-gray-200 bg-white text-sm shadow-md transition duration-100 ease-in [--anchor-gap:4px] focus:outline-none data-[closed]:opacity-0"
            >
              {options.map((option) => (
                <Listbox.Option
                  key={option.configName}
                  className={({ active, selected }) => {
                    return ` ${active ? 'bg-blue-50' : ''} ${selected ? 'font-medium text-mainblue' : 'text-gray-900'} cursor-pointer select-none px-4 py-2`;
                  }}
                  value={option.configName}
                >
                  {option.label}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </div>
        </Listbox>
      </div>

      <GenericModal
        isOpen={pendingConfigName !== null}
        setIsOpen={(open) => {
          if (!open) closeConfirmation();
        }}
        title="Change Raptor config"
        preventClose={saveConfig.isPending}
        closeOnOutsideClick={!saveConfig.isPending}
      >
        <div className="space-y-3 text-sm text-gray-700">
          <p>
            Move <span className="font-medium text-gray-900">{userName}</span>{' '}
            from <span>&quot;{currentConfigLabel}&quot;</span> to{' '}
            <span>&quot;{pendingConfigLabel}&quot;</span>. They will see the new
            pages the next time they sign in.
          </p>
          {isCurrentUser && (
            <p>
              This is your own account. Sign out and back in before you rely on
              the new pages.
            </p>
          )}
          {saveError && (
            <span className="text-sm text-red-600">{saveError}</span>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={closeConfirmation}
              disabled={saveConfig.isPending}
              className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                if (pendingConfigName) saveConfig.mutate(pendingConfigName);
              }}
              disabled={saveConfig.isPending || !pendingConfigName}
              className="rounded bg-mainblue px-3 py-1.5 text-sm text-white hover:bg-blue-600 active:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {saveConfig.isPending ? 'Saving...' : 'Confirm'}
            </button>
          </div>
        </div>
      </GenericModal>
    </>
  );
};

export default UciluxUserConfigSelector;
